// src/utils/laCourtAccess.js

// Debounce mechanism to prevent rapid successive clipboard operations
let lastClipboardOperation = 0;
const CLIPBOARD_DEBOUNCE_MS = 500; // 500ms debounce

/**
 * Opens the LA Court website and copies the case number to clipboard
 * @param {string} caseNumber - The case number to copy
 * @returns {Promise<void>}
 */
export const openLACourtWithCaseNumber = (caseNumber) => {
  return new Promise(async (resolve, reject) => {
    const mainConsole = window.console;
    mainConsole.log('🚀 Opening LA Court website for case:', caseNumber);

    // Debounce clipboard operations to prevent race conditions
    const now = Date.now();
    const timeSinceLastOperation = now - lastClipboardOperation;
    if (timeSinceLastOperation < CLIPBOARD_DEBOUNCE_MS) {
      const waitTime = CLIPBOARD_DEBOUNCE_MS - timeSinceLastOperation;
      mainConsole.log(
        `⏳ Debouncing clipboard operation, waiting ${waitTime}ms`
      );
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
    lastClipboardOperation = Date.now();

    // Copy case number to clipboard
    const copyToClipboard = () => {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(caseNumber);
      } else {
        // Fallback for older browsers
        return new Promise((resolve, reject) => {
          const textArea = document.createElement('textarea');
          textArea.value = caseNumber;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          textArea.style.opacity = '0';
          textArea.setAttribute('readonly', '');
          document.body.appendChild(textArea);

          // Clear any existing selection
          if (window.getSelection) {
            window.getSelection().removeAllRanges();
          }

          textArea.focus();
          textArea.select();
          textArea.setSelectionRange(0, 99999); // For mobile devices

          try {
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            if (successful) {
              resolve();
            } else {
              reject(new Error('execCommand copy failed'));
            }
          } catch (err) {
            document.body.removeChild(textArea);
            reject(err);
          }
        });
      }
    };

    try {
      // Wait for clipboard operation to complete before proceeding
      await copyToClipboard();
      mainConsole.log('✅ Case number copied to clipboard:', caseNumber);

      // Verify the clipboard content (if possible)
      if (navigator.clipboard && navigator.clipboard.readText) {
        try {
          const clipboardContent = await navigator.clipboard.readText();
          if (clipboardContent !== caseNumber) {
            mainConsole.warn(
              '⚠️ Clipboard verification failed! Expected:',
              caseNumber,
              'Got:',
              clipboardContent
            );
          } else {
            mainConsole.log('✅ Clipboard verification successful');
          }
        } catch (verifyError) {
          mainConsole.log(
            'ℹ️ Could not verify clipboard content (permission denied)'
          );
        }
      }

      // Open the LA Court website in a new window
      try {
        window.open(
          'https://media.lacourt.org/lascmediaproxy/#/caseaccess',
          '_blank',
          'noopener,noreferrer'
        );
        mainConsole.log('✅ LA Court website opened in new tab');
      } catch (error) {
        mainConsole.error('❌ Error opening window:', error);
      }

      resolve();
    } catch (err) {
      mainConsole.warn('⚠️ Could not copy to clipboard:', err.message);
      mainConsole.log('📋 Case number to copy manually:', caseNumber);

      // Still open the website even if clipboard fails
      try {
        window.open(
          'https://media.lacourt.org/lascmediaproxy/#/caseaccess',
          '_blank',
          'noopener,noreferrer'
        );
        mainConsole.log('✅ LA Court website opened in new tab');
      } catch (error) {
        mainConsole.error('❌ Error opening window:', error);
      }

      // Resolve with error so the calling code can handle it
      reject(err);
    }
  });
};

/**
 * Copies case number to clipboard
 * @param {string} caseNumber - The case number to copy
 * @returns {Promise<void>}
 */
export const copyCaseNumberToClipboard = (caseNumber) => {
  return navigator.clipboard
    .writeText(caseNumber)
    .then(() => {
      console.log('Case number copied to clipboard');
    })
    .catch((err) => {
      console.error('Failed to copy case number:', err);
      throw err;
    });
};
